import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, ChatMessageView, Icon, useToast } from '@aidkitorg/component-library';
import { ConfigurationContext } from '../Context';
import { useMarkdown } from '../Util';
import { ChatEvent, useChatChannel } from '../AICoach/ChatChannel';
import { Root } from '@aidkitorg/types/lib/survey';
import { State } from '@aidkitorg/typesheets/lib/collab';

export default function CoachTab(props: { survey: Root, state: State }) {
  const [inputText, setInputText] = useState<string>("");
  const config = useContext(ConfigurationContext);
  const { toast } = useToast();

  const distroEditor = useMemo(() => document.querySelector('distro-editor')?.shadowRoot, [props.survey]);

  const channel = useChatChannel('distro');

  useEffect(() => channel.setEnabled(true), []);

  /* Distro is a Shadow DOM. This means that it is actually 
  *  impossible to interrogate the internal React Components from outside the distro-editor component.
  *
  *  Things like Context Providers simply don't get passed in, which would have made life WAY simpler.
  *
  *  Fortunately, this Shadow DOM is set to 'open', meaning we can at least interrogate the raw HTML Element objects.
  *
  *  To work around this, this callback employs a few things:
  *  - Shadow DOM is visible to the root document's query selector
  *  - Once we have a handle of the Shadow DOM, we can reach into it.
  *  - Every HTML Element within the Distro DOM is related to a Tracked Object. 
  *    As a result, we can get the Tracked Object's ID from the `data-tracker-id` field.
  *  - Once we have the Tracked Object ID, we can get the state of that object using the provided Distro State.
  */
  const indicateInterest = useCallback(async (e: MouseEvent) => {
    const elem = distroEditor?.elementFromPoint(e.x, e.y);

    if (!elem || channel?.status.status !== 'open') return;

    let node: Element | null | undefined = elem;
    let trackerId: string | null | undefined = node?.getAttribute('data-tracker-id');
    while (!trackerId && node) {
      node = node.parentElement;
      trackerId = node?.getAttribute('data-tracker-id');
    }

    // nothing to do if we don't have a tracked object.
    if (!trackerId) {
      return;
    }

    const obj = props.state?.workingSet[trackerId];

    let content = obj?.read(true);

    if (!content) {
      return;
    }

    if (typeof content !== 'string') {
      content = JSON.stringify(content)
    }

    // safeguard against large sections being selected,
    // as it will make the chatbot incapable of giving coherent responses.
    if ((content.length / 4) > 10_000) {
      toast({
        type: 'foreground',
        description: `content selected was ~${Math.floor(content.length / 4)} words, which is too large for the chat. try selecting a smaller section`,
        variant: 'error'
      });
      return;
    }

    channel.sendChatMessage('system', `tell me about this part of the survey: ${content}`, true);

  }, [distroEditor, channel, props.state]);

  useEffect(() => {
    distroEditor?.addEventListener('dblclick', indicateInterest as any);
    return () => distroEditor?.removeEventListener('dblclick', indicateInterest as any);

  }, [distroEditor, indicateInterest]);

  useEffect(() => {
    console.log(props.state.unpublishedChanges);
    if (channel.status.status === 'open') {
      channel.updateSession({
        instructions: `
          you are helping guide an editor ${props.state.currentAuthor?.name ? `(named ${props.state.currentAuthor?.name})` : ''} in understanding the impact of their changes as they are made.    
          here are their unpublished changes: ${JSON.stringify(props.state?.unpublishedChanges ?? {})}
          when asked about their changes, relate them using plain language and keep responses short.
        `
      });
    } else {
      console.log('channel status:', channel);
    }
  }, [channel.status, props.state.unpublishedChanges?.length]);

  function MessageContent({ message }: { message: ChatEvent }) {
    const content = message.content?.map(c => c.text ?? c.transcript).join('');
    return useMarkdown(content);
  }

  const messageBubbles: Parameters<typeof ChatMessageView>[0]['transcript'] = useMemo(() => {
    let items: Parameters<typeof ChatMessageView>[0]['transcript'] = [];

    items.push({
      role: 'assistant',
      meta: {
        time: Date.now(),
      },
      content: <span>Try double-clicking on something, or asking about your pending changes!</span>,
    });

    items = items.concat(channel?.messages.filter(t => !t.meta.hidden).map(m => ({
      role: m.role,
      meta: m.meta,
      content: <MessageContent message={m} />
    })) || []);

    return items;
  }, [channel?.messages, focus]);

  const handleChatSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (inputText) {
      channel?.sendChatMessage('user', inputText);
    }
    setInputText('');
  }

  return <div>
    <ChatMessageView
      transcript={messageBubbles}
      roles={{
        assistant: {
          side: 'left',
          color: 'bg-indigo-100',
          icon: <img src="https://i.postimg.cc/GmczHr3p/avatar-opal.png" alt="Opal" className="rounded-full" />
        },
        user: {
          side: 'right',
          icon: <span className="p-2 bg-indigo-300 rounded-full">{(config?.user?.name?.substring(0, 2)) ?? 'U'}</span>,
          color: 'bg-gray-100',
        }
      }}
      autoScroll={true}
    />

    <div className="border-t border-gray-200 p-3">
      <div className="flex items-center gap-2">
        <form
          className="flex flex-1 gap-2"
          onSubmit={handleChatSubmit}>
          <input
            type="text"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Type a message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <Button
            type="submit"
            isDisabled={!inputText || channel?.status.status !== 'open'}
          >
            <Icon
              className="-rotate-45 -mt-1 -mr-1"
              name="PaperAirplaneIcon" />
          </Button>
          <Button
            type="reset"
            title="Restart Chat"
            onClick={() => {
              channel.restart();
            }}
          >
            <Icon
              name="FireIcon"
            />
          </Button>

        </form>
      </div>
    </div>
  </div>
}
